import { Add as AddIcon } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { AddOrderItemRequest } from "../../../types/admin";

interface AddOrderItemModalProps {
  open: boolean;
  onClose: () => void;
  orderId: string;
  onSave: (request: AddOrderItemRequest) => Promise<void>;
}

const AddOrderItemModal: React.FC<AddOrderItemModalProps> = ({
  open,
  onClose,
  orderId,
  onSave,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<Omit<AddOrderItemRequest, "orderId">>({
    productId: "",
    title: "",
    price: 0,
    quantity: 1,
    image: "",
    retailer: "Amazon",
  });

  const handleSubmit = async () => {
    if (!formData.productId || !formData.title || !formData.price || !formData.image) {
      setError(t("admin.orders.fillRequiredFields"));
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await onSave({
        orderId,
        ...formData,
        cargoPrice: formData.cargoPrice || undefined,
        estimatedWeight: formData.estimatedWeight || undefined,
        notes: formData.notes || undefined,
      });

      handleClose();
    } catch (error) {
      console.error("Error adding item:", error);
      setError(t("admin.orders.failedToAddItem"));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      productId: "",
      title: "",
      price: 0,
      quantity: 1,
      image: "",
      retailer: "Amazon",
    });
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AddIcon />
          {t("admin.orders.addOrderItem")}
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label={t("admin.orders.productId")}
              value={formData.productId}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, productId: e.target.value }))
              }
              disabled={loading}
              placeholder="B0XXXXXXXXX"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label={t("admin.orders.title")}
              value={formData.title}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, title: e.target.value }))
              }
              disabled={loading}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              required
              label={t("common.price")}
              type="number"
              value={formData.price || ""}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  price: parseFloat(e.target.value) || 0,
                }))
              }
              disabled={loading}
              inputProps={{ step: "0.01", min: "0" }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              required
              label={t("admin.orders.quantity")}
              type="number"
              value={formData.quantity}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  quantity: parseInt(e.target.value, 10) || 1,
                }))
              }
              disabled={loading}
              inputProps={{ step: "1", min: "1" }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label={t("admin.retailer")}
              value={formData.retailer || ""}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, retailer: e.target.value }))
              }
              disabled={loading}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label={t("admin.orders.imageUrl")}
              value={formData.image}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, image: e.target.value }))
              }
              disabled={loading}
              placeholder="https://..."
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t("admin.orders.cargoPrice")}
              type="number"
              value={formData.cargoPrice ?? ""}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  cargoPrice:
                    e.target.value === "" ? undefined : parseFloat(e.target.value),
                }))
              }
              disabled={loading}
              inputProps={{ step: "0.01", min: "0" }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t("admin.orders.estimatedWeight")}
              type="number"
              value={formData.estimatedWeight ?? ""}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  estimatedWeight:
                    e.target.value === "" ? undefined : parseFloat(e.target.value),
                }))
              }
              disabled={loading}
              inputProps={{ step: "0.01", min: "0" }}
              helperText={t("admin.orders.weightInKg")}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t("common.notes")}
              multiline
              rows={3}
              value={formData.notes || ""}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, notes: e.target.value }))
              }
              disabled={loading}
              placeholder={t("admin.itemNotesPlaceholder")}
            />
          </Grid>
        </Grid>

        {formData.price > 0 && formData.quantity > 0 && (
          <Box sx={{ mt: 2, p: 1.5, backgroundColor: "#f5f5f5", borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {t("common.subtotal")}: ${(formData.price * formData.quantity).toFixed(2)}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {t("common.cancel")}
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? t("admin.saving") : t("admin.orders.addItem")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddOrderItemModal;
